// TODO fix above later
import { Add } from '@mui/icons-material';
import { Button, Tooltip, Typography } from '@mui/material';
import type { CellContext } from '@tanstack/react-table';
import type { BaseSyntheticEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { AssetDuration } from '@/components/asset-duration';
import AssetPlayer from '@/components/SeriesContainer/Seasons/AssetPlayer';
import VNAssetEditor from '@/components/VNAssetEditor/VNAssetEditor';
import type { VNLibraryArgs } from '@/components/VNLibrary/VNLibrary';
import VNLibrary, { VNLibraryType } from '@/components/VNLibrary/VNLibrary';
import VNPortlet from '@/components/VNPortlet/VNPortlet';
import TableActions from '@/components/VNTable/TableActions';
import VNTable from '@/components/VNTable/VNTable';
import { useProgress } from '@/contexts/ProgressProvider';
import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import { MessageType, PushNotificationEventType } from '@/enums';
import { useModalContext } from '@/modals/VNModalProvider';
import type { Item } from '@/models';
import type { LinkedAssetInfo } from '@/models/channel';
import { AccessLevelType } from '@/models/channel';
import { enhanceFetchedAssets } from '@/pages/series/series-listing/enhanceFetchedAsset';
import { FuelEntityType, FuelPortalService } from '@/services';
import { useAppDispatch } from '@/store';
import { AssetForm } from '@/store/forms';
import { applicationDataSelector } from '@/store/slices/application-data';
import { UpdateAssetForm } from '@/store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';
import itemUtils from '@/utils/itemUtils';

import FuelAssetThumbnail from '../FuelAssetThumbnail/FuelAssetThumbnail';

type Props = {
  title: string;
  linkedAssetType: 'Trailer' | 'Extra';
};

const Thumbnail = memo((cell: CellContext<Item, string>) => <FuelAssetThumbnail item={cell.row.original} />);

const AssetList = ({ title, linkedAssetType }: Props) => {
  const { vnConfirm } = useAppUtils();
  const { setProgress } = useProgress();

  const { setValue, getValues, watch } = useFormContext();
  const linkedAssets = watch(`tmp.${linkedAssetType}`) || [];

  const [loading, setLoading] = useState(true);
  const [showVNLibrary, setShowVNLibrary] = useState(false);
  const [playAsset, setPlayAsset] = useState<Item>(null);
  const modal = useModalContext();
  const dispatch = useAppDispatch();
  const { lastJsonMessage } = useWebSocketContext();
  const rowRefs = useRef([]);

  const { ouData } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);

  /** Delete asset */
  const deleteAsset = useCallback(
    async (asset: Item) => {
      if (!asset || !(await vnConfirm(`Do you want to remove the ${linkedAssetType} "${asset.title}"?`))) {
        return;
      }

      const i = linkedAssets.findIndex((a: Item) => a.id === asset.id);
      if (i > -1) {
        // remove from array
        linkedAssets.splice(i, 1);
        setValue(`tmp.${linkedAssetType}`, [...linkedAssets]);
      }

      // remove the display
      const linkedAssetList = getValues('linkedAssets');
      const j = linkedAssetList.findIndex((a: LinkedAssetInfo) => a.assetId === asset.id && a.type === linkedAssetType);
      if (j > -1) {
        // remove from array
        linkedAssetList.splice(j, 1);
        setValue('linkedAssets', [...linkedAssetList]);
      }
    },
    [linkedAssets]
  );

  const onAddAsset = useCallback(
    async (type: string, item?: Item) => {
      if (type === 'add') {
        setShowVNLibrary(true);
      } else if (item && ['edit', 'view'].includes(type)) {
        setProgress(true);
        const asset: Item = await FuelPortalService.getEntityById(FuelEntityType.Clip, item.id);
        if (type === 'edit') {
          const assetFormValue = await AssetForm.createFromData(asset, permissions);
          dispatch(UpdateAssetForm(assetFormValue));

          // only used the model, removed the flag. check with Adam later
          modal.onOpen('Edit Asset', 'xl', <VNAssetEditor addToList={handleAssetEditorSaved} />, item.id);
        } else if (type === 'view') {
          setPlayAsset(asset);
        }
      } else if (type === 'delete' && item) {
        await deleteAsset(item);
      }
      setProgress(false);
    },
    [permissions, ouData, deleteAsset]
  );

  const onTableAction = (event: React.MouseEvent, action: string, item: Item) => {
    event.stopPropagation(); // Prevent row click events
    onAddAsset(action, item);
  };

  const TitleCell = useCallback(
    ({ row }: CellContext<Item, string>) => (
      <Tooltip title={row.original.title} placement="top" arrow>
        <Typography className="title" onClick={() => onAddAsset('edit', row.original)}>
          {row.original.title}
        </Typography>
      </Tooltip>
    ),
    [onAddAsset]
  );
  const DurationCell = useCallback(({ row }: CellContext<Item, string>) => <AssetDuration item={row.original} />, []);
  const ActionsCell = useCallback(
    ({ row }: CellContext<Item, string>) => (
      <TableActions row={row} onAction={onTableAction} actions={['view', 'edit', 'delete']} />
    ),
    [onTableAction]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'thumbnailSrc',
        header: 'THUMBNAIL',
        cell: Thumbnail,
      },
      {
        accessorKey: 'title',
        header: 'TITLE',
        cell: TitleCell,
        meta: {
          sortable: false,
          style: {
            textAlign: 'left',
          },
        },
      },
      {
        accessorKey: 'data.accessSettings.effectiveAccessLevel',
        header: 'Access',
        cell: (cell) => cell.row.original.data?.accessSettings?.effectiveAccessLevel || AccessLevelType.Free,
        meta: {
          sortable: false,
          style: {
            textAlign: 'center',
          },
        },
      },
      {
        accessorKey: 'duration',
        header: 'DURATION',
        size: 80,
        meta: {
          sortable: false,
          style: {
            textAlign: 'center',
          },
        },
        cell: DurationCell,
      },
      {
        accessorKey: 'actions',
        header: 'ACTIONS',
        size: 120,
        meta: {
          sortable: false,
          style: {
            textAlign: 'center',
          },
        },
        cell: ActionsCell,
      },
    ],
    [onAddAsset, onTableAction]
  );

  const initData = useCallback(async () => {
    if (linkedAssets.length > 0) {
      setLoading(false);
      return;
    }

    try {
      const linkedAssetsList = getValues('linkedAssets') || [];
      const filteredAssetIds = linkedAssetsList
        .filter((asset: LinkedAssetInfo) => asset.type === linkedAssetType)
        .map((asset: LinkedAssetInfo) => asset.assetId);

      if (filteredAssetIds.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const rawItems: Item[] = await FuelPortalService.batchGet(FuelEntityType.Clip, filteredAssetIds);
      const thumbnails = await itemUtils.fetchLinkedDisplayThumbnails(rawItems);
      let enhancedAssets = enhanceFetchedAssets(rawItems, permissions, {}, true, [], [], ouData, thumbnails);
      enhancedAssets = enhancedAssets.filter((item) => !item.isDeleted);

      setValue(`tmp.${linkedAssetType}`, enhancedAssets);
    } catch (e) {
      console.error('Failed to load asset', e);
    } finally {
      setLoading(false);
    }
  }, [permissions, ouData, linkedAssets.length]);

  const handleMove = useCallback(
    (prevItem: Item, newItem: Item) => {
      // save all other types of linked assets as is
      const otherLinkedAssets =
        getValues('linkedAssets')?.filter((asset: LinkedAssetInfo) => asset.type !== linkedAssetType) || [];
      const thisLinkedAssets =
        getValues('linkedAssets')?.filter((asset: LinkedAssetInfo) => asset.type === linkedAssetType) || [];

      const prevIndex = thisLinkedAssets.findIndex((a: LinkedAssetInfo) => a.assetId === prevItem.id);
      const newIndex = thisLinkedAssets.findIndex((a: LinkedAssetInfo) => a.assetId === newItem.id);
      if (prevIndex === -1 || newIndex === -1) {
        console.error('Invalid indices for move operation');
        return;
      }

      const [movedAsset] = thisLinkedAssets.splice(prevIndex, 1);
      thisLinkedAssets.splice(newIndex, 0, movedAsset);

      setValue('linkedAssets', [...otherLinkedAssets, ...thisLinkedAssets]);

      // also update tmp linked assets for display
      const tmpLinkedAssets = [...linkedAssets];
      const [movedTmpAsset] = tmpLinkedAssets.splice(prevIndex, 1);
      tmpLinkedAssets.splice(newIndex, 0, movedTmpAsset);
      setValue(`tmp.${linkedAssetType}`, tmpLinkedAssets);
    },
    [linkedAssetType, linkedAssets]
  );

  const addAsset = useCallback(
    async (event: BaseSyntheticEvent, value: VNLibraryArgs) => {
      const { items } = value;
      if (!items || items.length === 0) {
        return;
      }

      const thumbnails = await itemUtils.fetchLinkedDisplayThumbnails(items);
      const enhancedItems = enhanceFetchedAssets(items, permissions, {}, true, [], [], ouData, thumbnails);
      setValue(`tmp.${linkedAssetType}`, [...linkedAssets, ...enhancedItems]);

      // save to form
      const linkedAssetsList: LinkedAssetInfo[] = getValues('linkedAssets') || [];
      setValue('linkedAssets', [
        ...linkedAssetsList,
        ...enhancedItems.map((i: Item) => ({
          type: linkedAssetType,
          assetId: i.id,
        })),
      ]);
    },
    [linkedAssetType, linkedAssets]
  );

  useEffect(() => {
    initData();
  }, [linkedAssetType]);

  const handleAssetEditorSaved = useCallback(
    async (item: Item) => {
      const i = linkedAssets.findIndex((a: Item) => a.id === item.id);
      if (i > -1) {
        const thumbnails = await itemUtils.fetchLinkedDisplayThumbnails([item]);
        const enhancedItem = enhanceFetchedAssets([item], permissions, {}, true, [], [], ouData, thumbnails)[0];
        linkedAssets[i] = { ...enhancedItem };
        setValue(`tmp.${linkedAssetType}`, [...linkedAssets]);
      }
    },
    [permissions, ouData, linkedAssets]
  );

  const readWebsocketMessage = async (message: any) => {
    if (
      message &&
      message.messageType === MessageType.Notification &&
      message.data &&
      message.data.eventType === PushNotificationEventType.IngestStatusUpdate
    ) {
      const clipId = message.data.id;
      if (clipId) {
        const asset = await FuelPortalService.getClipById(clipId);
        await handleAssetEditorSaved(asset);
      }
    }
  };

  useEffect(() => {
    if (!lastJsonMessage) return;
    const processMessage = async () => {
      try {
        await readWebsocketMessage(lastJsonMessage);
      } catch (error) {
        console.error('Error processing websocket message:', error);
      }
    };
    processMessage();
  }, [lastJsonMessage]);

  return (
    <VNPortlet
      className="asset-list"
      title={title}
      actions={
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={() => onAddAsset('add')}
          startIcon={<Add />}
          style={{ width: '148px', height: '32px' }}
        >
          Add {title}
        </Button>
      }
    >
      <div className="asset-table-wrapper">
        <VNTable
          sortable={handleMove}
          columns={columns}
          data={linkedAssets}
          hidePagination
          loading={loading}
          loadingRows={5}
          rowRefs={rowRefs}
        />
      </div>
      {showVNLibrary && (
        <VNLibrary multiple libraryType={VNLibraryType.Video} onItemSelected={addAsset} onClose={() => setShowVNLibrary(false)} />
      )}
      {playAsset && <AssetPlayer asset={playAsset} handleClose={() => setPlayAsset(null)} />}
    </VNPortlet>
  );
};

export default AssetList;
